export default (initialState = {}) => ({
  // State
  isRevealed: false,
  userHasInteracted: false,
  isInstructionalAnimationVisible: false,

  // Config
  instructionsVideo: initialState.instructionsVideo || 'discountScratchCardAnimation',
  clearThresholdPercent: initialState.clearThresholdPercent || 45,
  scratchBrushRadius: initialState.scratchBrushRadius || 18,
  coverColor: initialState.coverColor || "#EFDCC6",
  previousPointerPosition: { x: 0, y: 0 },

  // Internal References
  canvasContext: null,
  dimensionObserver: null,
  _exitHandler: null,
  _blurHandler: null,

  init() {
    this.clearThresholdPercent = this.getNumericAttribute("threshold", 45);
    this.scratchBrushRadius = this.getNumericAttribute("brush", 50);

    if (!this.$refs.stage || !this.$refs.canvas) return;
    this.canvasContext = this.$refs.canvas.getContext("2d", { willReadFrequently: true });

    this.$nextTick(() => {
      this.syncCanvasDimensions();
      this.renderCoverLayer();
      this.initializeInteractionListeners();
      this.setupResizeTracking();
      this.setupViewportExitDetection();

      // Show instructional video if no interaction occurs
      setTimeout(() => {
        if (!this.userHasInteracted && !this.isRevealed) {
          this.isInstructionalAnimationVisible = true;
        }
      }, 500);
    });

    // Auto-reveal
    setTimeout(() => {
      if (!this.userHasInteracted && !this.isRevealed) {
        this.revealContent();
      }
    }, 8000);
  },

  destroy() {
    window.removeEventListener("mouseout", this._exitHandler);
    window.removeEventListener("blur", this._blurHandler);
    if (this.dimensionObserver) this.dimensionObserver.disconnect();
  },

  setupViewportExitDetection() {
    this._exitHandler = (e) => {
      // clientY < 0 indicates mouse left the top of the browser window
      if (e.clientY < 0 && !this.isRevealed) {
        this.revealContent();
      }
    };

    this._blurHandler = () => {
      if (!this.isRevealed) this.revealContent();
    };

    window.addEventListener("mouseout", this._exitHandler);
    window.addEventListener("blur", this._blurHandler);
  },

  setupResizeTracking() {
    if (!("ResizeObserver" in window)) return;
    this.dimensionObserver = new ResizeObserver(() => {
      if (this.isRevealed) return;
      this.syncCanvasDimensions();
      this.renderCoverLayer();
    });
    this.dimensionObserver.observe(this.$refs.stage);
  },

  syncCanvasDimensions() {
    const canvas = this.$refs.canvas;
    const bounds = this.$refs.stage.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    canvas.width = Math.floor(bounds.width * dpr);
    canvas.height = Math.floor(bounds.height * dpr);

    this.canvasContext.setTransform(1, 0, 0, 1, 0, 0);
    this.canvasContext.scale(dpr, dpr);
    this.canvasContext.lineCap = "round";
    this.canvasContext.lineJoin = "round";
  },

  renderCoverLayer() {
    if (!this.canvasContext) return;
    this.canvasContext.globalCompositeOperation = "source-over";
    this.canvasContext.fillStyle = this.coverColor;
    this.canvasContext.fillRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);
  },

  initializeInteractionListeners() {
    const canvas = this.$refs.canvas;
  
    // False listener to allow preventDefault
    canvas.addEventListener('pointerdown', (event) => {
      if (this.isRevealed) return;
  
      this.userHasInteracted = true;
      this.isInstructionalAnimationVisible = false;
  
      // For iOS: capture the pointer to the canvas
      canvas.setPointerCapture(event.pointerId);
      this.previousPointerPosition = this.getCoordinates(event);
  
      const handleMove = (e) => {
        // Prevent iOS from trying to scroll or "bounce" the page
        if (e.cancelable) e.preventDefault(); 
        this.performScratch(e);
      };
  
      const handleUp = (e) => {
        canvas.releasePointerCapture(e.pointerId);
        canvas.removeEventListener("pointermove", handleMove);
        this.evaluateClearanceProgress();
      };
  
      canvas.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleUp, { once: true });
    }, { passive: false });
  },

  getCoordinates(event) {
    const rect = this.$refs.canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  },

  performScratch(event) {
    const currentPos = this.getCoordinates(event);
    const ctx = this.canvasContext;

    ctx.globalCompositeOperation = "destination-out";
    ctx.lineWidth = this.scratchBrushRadius * 2;

    ctx.beginPath();
    ctx.moveTo(this.previousPointerPosition.x, this.previousPointerPosition.y);
    ctx.lineTo(currentPos.x, currentPos.y);
    ctx.stroke();

    this.previousPointerPosition = currentPos;
  },

  evaluateClearanceProgress() {
    const canvas = this.$refs.canvas;
    const pixelData = this.canvasContext.getImageData(0, 0, canvas.width, canvas.height).data;

    let transparentPixels = 0;
    const step = 64;
    const totalPixelsChecked = pixelData.length / 4 / step;

    for (let i = 3; i < pixelData.length; i += step * 4) {
      if (pixelData[i] < 128) transparentPixels++;
    }

    const percentage = (transparentPixels / totalPixelsChecked) * 100;
    if (percentage >= this.clearThresholdPercent) {
      this.revealContent();
    }
  },

  revealContent() {
    if (this.isRevealed) return;
    this.isRevealed = true;

    window.removeEventListener("mouseout", this._exitHandler);
    window.removeEventListener("blur", this._blurHandler);

    // Completely clear the canvas for the final reveal
    if (this.canvasContext) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.canvasContext.clearRect(
        0,
        0,
        this.$refs.canvas.width / dpr,
        this.$refs.canvas.height / dpr,
      );
    }

    // Stop and hide the instructional animation
    this.isInstructionalAnimationVisible = false;

    const demoElement = this.$root.querySelector(".pipe-discount-scratch-card-demo-inner");
    if (demoElement) {
      demoElement.dispatchEvent(new CustomEvent("animation:stop"));
    }

    this.$refs.cover.classList.add("is-cleared");
    this.$dispatch("pipe:discount-scratch-card-revealed");
  },

  getNumericAttribute(key, fallback) {
    const val = Number(this.$root.dataset[key]);
    return Number.isFinite(val) ? val : fallback;
  },
});